import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaImage, FaVideo, FaFileAlt, FaSmile, FaLink, FaTimes, FaMapMarkerAlt, FaLightbulb, FaFlask, FaGraduationCap, FaRocket, FaHandshake, FaDna } from 'react-icons/fa';
import FeedAvatar from './FeedAvatar';
import PrivacyModal from './PrivacyModal';
import './feed.shared.css';

const DEFAULT_CATEGORY_STYLES = {
    articulo: { color: '#3b82f6', label: '📄 Artículo Científico' },
    evento: { color: '#FF6600', label: '📅 Evento' },
    discusion: { color: '#FF6600', label: '👥 Discusión' },
    publicacion: { color: '#10b981', label: '📢 Publicación' },
};

const DEFAULT_GROUPS = [
    { id: 1, name: 'Ensayos Fase III', icon: '🧬' },
    { id: 2, name: 'Bioética Clínica', icon: '⚖️' },
    { id: 3, name: 'Oncología Traslacional', icon: '🔬' },
];

const FeedComposer = ({
    user,
    onPublish,
    placeholder,
    showCategories = false,
    categoryStyles = DEFAULT_CATEGORY_STYLES,
    groups = DEFAULT_GROUPS,
    onShowFeedback,
}) => {
    const { t } = useTranslation();
    const [postCategory, setPostCategory] = useState('discusion');
    const [postText, setPostText] = useState('');
    const [title, setTitle] = useState('');
    const [doi, setDoi] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [eventTime, setEventTime] = useState('');
    const [eventModality, setEventModality] = useState('virtual'); // 'virtual' or 'onsite'
    const [eventCountry, setEventCountry] = useState('');
    const [eventCity, setEventCity] = useState('');
    const [eventLocation, setEventLocation] = useState('');
    const [feeling, setFeeling] = useState('');
    const [showFeelingList, setShowFeelingList] = useState(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

    // File upload state
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileType, setFileType] = useState(null); // 'photo', 'video', 'file'
    const [filePreview, setFilePreview] = useState(null);

    const photoInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const fileInputRef = useRef(null);

    const feelings = [
        { icon: <FaSmile style={{ color: '#FF6600' }} />, label: 'Feliz' },
        { icon: <FaLightbulb style={{ color: '#fcd34d' }} />, label: 'Inspirado' },
        { icon: <FaFlask style={{ color: '#3b82f6' }} />, label: 'En el laboratorio' },
        { icon: <FaGraduationCap style={{ color: '#10b981' }} />, label: 'Estudiando' },
        { icon: <FaRocket style={{ color: '#ef4444' }} />, label: 'Lanzando' },
        { icon: <FaHandshake style={{ color: '#8b5cf6' }} />, label: 'Colaborando' },
        { icon: <FaDna style={{ color: '#ec4899' }} />, label: 'Investigando' },
    ];

    const COUNTRIES = [
        { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
        { code: 'ES', name: 'España', flag: '🇪🇸' },
        { code: 'MX', name: 'México', flag: '🇲🇽' },
        { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
        { code: 'CL', name: 'Chile', flag: '🇨🇱' },
        { code: 'PE', name: 'Perú', flag: '🇵🇪' },
        { code: 'US', name: 'EE.UU.', flag: '🇺🇸' },
        { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
        { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
        { code: 'PA', name: 'Panamá', flag: '🇵🇦' },
        { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
    ];

    const [showCountryList, setShowCountryList] = useState(false);

    React.useEffect(() => {
        if (user?.profile?.country && !eventCountry) {
            setEventCountry(user.profile.country);
        }
    }, [user, eventCountry]);

    const allowedExts = {
        photo: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
        video: ['mp4', 'webm', 'mov'],
        file: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar']
    };

    const displayName = user?.profile
        ? `${user.profile.firstName} ${user.profile.lastName}`
        : (user?.name || user?.email || 'Usuario');

    const photoUrl = user?.profile?.photoUrl || null;

    const composerPlaceholder = placeholder
        || t('community.feed.composerPlaceholder', 'Comparte algo con la comunidad...');

    const isPublishDisabled = () => {
        if (postCategory === 'articulo') {
            return !title.trim() || !postText.trim() || !doi.trim();
        }
        if (postCategory === 'evento') {
            return !title.trim() || !postText.trim() || !eventDate;
        }
        if (postCategory === 'discusion' || postCategory === 'publicacion') {
            return !postText.trim() && !selectedFile;
        }
        return !postText.trim() && !selectedFile;
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const ext = file.name.split('.').pop().toLowerCase();
        if (!allowedExts[type].includes(ext)) {
            onShowFeedback?.('Archivo no permitido', `Tipo de archivo no permitido para ${type}. Permitidos: ${allowedExts[type].join(', ')}`, 'error');
            e.target.value = null;
            return;
        }

        setSelectedFile(file);
        setFileType(type);

        if (type === 'photo') {
            const reader = new FileReader();
            reader.onloadend = () => setFilePreview(reader.result);
            reader.readAsDataURL(file);
        } else {
            setFilePreview(null);
        }
    };

    const removeFile = () => {
        setSelectedFile(null);
        setFileType(null);
        setFilePreview(null);
        if (photoInputRef.current) photoInputRef.current.value = null;
        if (videoInputRef.current) videoInputRef.current.value = null;
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const handlePublishClick = () => {
        if (isPublishDisabled()) return;
        setIsPrivacyModalOpen(true);
    };

    const handlePrivacyConfirm = (privacyData) => {
        const { privacy, allowedGroups } = privacyData;
        const metadata = {
            privacy,
            allowedGroups
        };

        if (postCategory === 'articulo') metadata.doi = doi;
        if (postCategory === 'evento') {
            metadata.date = eventDate;
            metadata.time = eventTime;
            metadata.modality = eventModality;
            metadata.location = eventLocation; // Platform (Virtual) or Venue (Onsite)
            if (eventModality === 'onsite') {
                metadata.country = eventCountry;
                metadata.city = eventCity;
            }
        }
        if (postCategory === 'discusion' || postCategory === 'publicacion') {
            metadata.feeling = feeling;
        }

        onPublish?.({
            type: postCategory,
            title: title.trim(),
            content: postText.trim(),
            metadata,
            media: selectedFile
        });

        // Reset
        setPostText('');
        setTitle('');
        setDoi('');
        setEventDate('');
        setEventTime('');
        setEventModality('virtual');
        setEventCountry('');
        setEventCity('');
        setEventLocation('');
        setFeeling('');
        setShowFeelingList(false);
        setIsPrivacyModalOpen(false);
        removeFile();
    };

    return (
        <div className="feed-composer">
            {/* Hidden Inputs */}
            <input
                type="file"
                ref={photoInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={e => handleFileChange(e, 'photo')}
            />
            <input
                type="file"
                ref={videoInputRef}
                style={{ display: 'none' }}
                accept="video/*"
                onChange={e => handleFileChange(e, 'video')}
            />
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={e => handleFileChange(e, 'file')}
            />

            <div className="feed-composer-top">
                <FeedAvatar name={displayName} size={42} src={photoUrl} />
                <div className="feed-composer-form-wrapper">
                    {/* Category pills — researcher only */}
                    {showCategories && (
                        <div className="feed-composer-cats">
                            {Object.entries(categoryStyles).map(([key, val]) => (
                                <button
                                    key={key}
                                    className={`feed-cat-pill ${postCategory === key ? 'active' : ''}`}
                                    style={postCategory === key ? { borderColor: val.color, color: val.color } : {}}
                                    onClick={() => setPostCategory(key)}
                                >
                                    {val.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Conditional Title Input */}
                    {(postCategory === 'articulo' || postCategory === 'evento') && (
                        <input
                            type="text"
                            className="feed-composer-title-input"
                            placeholder={postCategory === 'articulo' ? "Título del Artículo" : "Título del Evento"}
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                        />
                    )}

                    <textarea
                        className="feed-composer-input"
                        placeholder={
                            postCategory === 'discusion' ? "Comparte el tema de discusión..." :
                                postCategory === 'publicacion' ? "Escribe tu publicación..." :
                                    "Descripción..."
                        }
                        value={postText}
                        onChange={e => setPostText(e.target.value)}
                        rows={3}
                        aria-label="Crear publicación"
                    />

                    {/* Media Preview Area */}
                    {selectedFile && (
                        <div className="composer-media-preview">
                            <button className="remove-media-btn" onClick={removeFile}>
                                <FaTimes />
                            </button>
                            {fileType === 'photo' && filePreview && (
                                <img src={filePreview} alt="Preview" className="media-preview-img" />
                            )}
                            {fileType === 'video' && (
                                <div className="media-preview-placeholder">
                                    <FaVideo className="icon" />
                                    <span>{selectedFile.name} (Video seleccionado)</span>
                                </div>
                            )}
                            {fileType === 'file' && (
                                <div className="media-preview-placeholder">
                                    <FaFileAlt className="icon" />
                                    <span>{selectedFile.name} (Archivo seleccionado)</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Article Specifics */}
                    {postCategory === 'articulo' && (
                        <div className="composer-extra-fields">
                            <div className="composer-field">
                                <FaLink className="icon" />
                                <input
                                    type="text"
                                    placeholder="DOI o Link del artículo"
                                    value={doi}
                                    onChange={e => setDoi(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Event Specifics */}
                    {postCategory === 'evento' && (
                        <div className="composer-extra-fields">
                            <div className="composer-extra-fields row">
                                <div className="composer-field">
                                    <input
                                        type="date"
                                        value={eventDate}
                                        onChange={e => setEventDate(e.target.value)}
                                    />
                                </div>
                                <div className="composer-field">
                                    <input
                                        type="time"
                                        value={eventTime}
                                        onChange={e => setEventTime(e.target.value)}
                                    />
                                </div>
                                <div className="composer-field modality-selector-container">
                                    <div className="modality-pills">
                                        <button 
                                            type="button"
                                            className={`modality-pill ${eventModality === 'virtual' ? 'active' : ''}`}
                                            onClick={() => setEventModality('virtual')}
                                        >
                                            <FaVideo className="icon-mod" /> Virtual
                                        </button>
                                        <button 
                                            type="button"
                                            className={`modality-pill ${eventModality === 'onsite' ? 'active' : ''}`}
                                            onClick={() => setEventModality('onsite')}
                                        >
                                            <FaMapMarkerAlt className="icon-mod" /> Presencial
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {eventModality === 'onsite' ? (
                                <>
                                    <div className="composer-extra-fields row" style={{ marginTop: '10px' }}>
                                        <div className="composer-field country-selector-field">
                                            <button 
                                                type="button"
                                                className="country-picker-trigger"
                                                onClick={() => setShowCountryList(!showCountryList)}
                                            >
                                                {eventCountry ? (
                                                    <>
                                                        <span className="flag">{COUNTRIES.find(c => c.name === eventCountry)?.flag}</span>
                                                        <span className="name">{eventCountry}</span>
                                                    </>
                                                ) : (
                                                    <span className="placeholder">Seleccionar País</span>
                                                )}
                                            </button>
                                            {showCountryList && (
                                                <div className="feeling-dropdown country-dropdown">
                                                    {COUNTRIES.map(c => (
                                                        <button
                                                            key={c.code}
                                                            className="feeling-option"
                                                            onClick={() => {
                                                                setEventCountry(c.name);
                                                                setShowCountryList(false);
                                                            }}
                                                        >
                                                            <span className="emoji">{c.flag}</span>
                                                            <span className="name">{c.name}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="composer-field">
                                            <input
                                                type="text"
                                                placeholder="Ciudad"
                                                value={eventCity}
                                                onChange={e => setEventCity(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="composer-field" style={{ marginTop: '10px' }}>
                                        <input
                                            type="text"
                                            placeholder="Dirección o Lugar (Ej: Auditorio Central)"
                                            value={eventLocation}
                                            onChange={e => setEventLocation(e.target.value)}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="composer-field" style={{ marginTop: '10px' }}>
                                    <input
                                        type="text"
                                        placeholder="Plataforma (Ej: Zoom, Google Meet, YouTube Live)"
                                        value={eventLocation}
                                        onChange={e => setEventLocation(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Discussion Specifics: Feeling */}
                    {feeling && (
                        <div className="feeling-badge">
                            Sentimiento: <span className="badge-icon">{feeling.icon}</span> {feeling.label}
                            <button className="remove-feeling" onClick={() => setFeeling('')}>×</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="feed-composer-actions">
                <div className="action-buttons">
                    <button className="feed-composer-btn" onClick={() => photoInputRef.current.click()}>
                        <FaImage style={{ color: '#22c55e' }} />
                        {t('community.feed.photo', 'Foto')}
                    </button>
                    <button className="feed-composer-btn" onClick={() => videoInputRef.current.click()}>
                        <FaVideo style={{ color: '#f59e0b' }} />
                        {t('community.feed.video', 'Video')}
                    </button>
                    <button className="feed-composer-btn" onClick={() => fileInputRef.current.click()}>
                        <FaFileAlt style={{ color: '#3b82f6' }} />
                        Archivo
                    </button>

                    <div className="feeling-selector-wrapper">
                        <button
                            className={`feed-composer-btn ${showFeelingList ? 'active' : ''}`}
                            onClick={() => setShowFeelingList(!showFeelingList)}
                        >
                            <FaSmile style={{ color: '#FF6600' }} />
                            {t('community.feed.feeling', 'Sentimiento')}
                        </button>

                        {showFeelingList && (
                            <div className="feeling-dropdown">
                                {feelings.map(f => (
                                    <button
                                        key={f.label}
                                        className="feeling-option"
                                        onClick={() => {
                                            setFeeling(f);
                                            setShowFeelingList(false);
                                        }}
                                    >
                                        <span className="emoji-icon">{f.icon}</span>
                                        <span className="label">{f.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <button
                    className="feed-publish-btn"
                    disabled={isPublishDisabled()}
                    onClick={handlePublishClick}
                >
                    {t('community.feed.publish', 'Publicar')}
                </button>
            </div>

            <PrivacyModal
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
                onConfirm={handlePrivacyConfirm}
                groups={groups}
            />
        </div>
    );
};

export default FeedComposer;
